import React, { useMemo } from 'react';
import moment from 'moment';
import GenericMetricsTable from '../GenericMetricsTable/GenericMetricsTable';
import { formatters } from '../GenericMetricsTable/utils';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { TABLE_COLUMNS, TABLE_HEADERS, CROSS_SITE_TABLE_COLUMNS, CROSS_SITE_TABLE_HEADERS } from './constants';
import TableCell from '../TableCell/TableCell';
import { AbTestGroupMode } from '@pdqdev/pdq-shared/dist/entities/AbTestGroupMode';
import { ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { getIsCheckoutProfileTest, getIsThemeTest, getIsThemeWidget, getWinnerButtonText } from '../../../../../utils';
import { AbTestTypeCategory, AbTestWidgetType } from '../../../../../models/AbTestCreationType';
import { SmartCart } from '../../../../ab-test-modal/hooks/useSmartCarts';
import { CheckoutProfile } from '../../../../ab-test-modal/hooks/useCheckoutProfiles';
import { DELETED_CHECKOUT_PROFILE_NAME } from '../../../../ab-test-modal/components/CheckoutProfileAllocationSection/checkoutProfileVariants';
import { AbTestWidget } from '../../../../../models/AbTestWidget';
import Button from '@app/react-common/components/button/Button';
import StatusText from '@app/react-common/components/status-text/StatusText';
import { IExtensionGroup } from '@app/pages/checkout-app/checkout-layout/main/components/extension-groups/models/extension-group.models';
import { getTooltipText } from '../GenericMetricsTable/tooltips';
import { formatSelectedExtensionNames } from '../../../../ab-test-modal/shared/abTestModalUtils';
import { getAbTestVariantPreviewString } from '../../../../../utils/AbTestingUtils';
import { VariantPreviewCopyButton } from './VariantPreviewCopyButton';

export interface Theme {
  id: number;
  name: string;
  updatedAt: string;
}

export interface ProductPage {
  id: number | null;
  name: string;
  internalDescription?: string;
  createdAt?: string;
}

export interface TestVariant {
  variant: string;
  description: string;
  checkoutGroupInfo?: string; // New field for "Test checkout group" text
  traffic: number;
  // Checkout/Extension metrics
  convRate?: number;
  arpc?: number;
  asr?: number;
  checkouts?: number;
  // Cross-Site metrics
  users?: number;
  statSig?: number;
  cvr?: number;
  arpu?: number;
  // Shared
  aov: number;
  isControl?: boolean;
  isWinning?: boolean;
}

export interface TestVariantsTableProps {
  variants: TestVariant[];
  isDraftMode: boolean;
  className?: string;
  isLoading?: boolean;
  showSetWinnerButton?: boolean;
  onSetWinner?: (variantId: number) => void;
  abTestVariants?: Array<{ id?: number; name: string; winnerAt?: Date | null }>;
  testType?: AbTestTypeCategory;
  abTestId?: number;
}

// Helper function to transform old variant data to new format
function describeExtensionVariantGroup(variantName: string): string {
  switch (variantName) {
    case 'A':
      return 'Control group';
    case 'B':
      return 'Test group';
    default:
      return `Variant ${variantName}`;
  }
}

export function transformAbTestVariantToTestVariant(
  variant: any,
  abTestType: AbTestTypeCategory | undefined,
  checkouts: any[],
  extensions: ICheckoutExtensions[],
  extensionGroups: IExtensionGroup[],
  metricsData?: any,
  abTestMode?: AbTestGroupMode,
  themes?: Theme[],
  productPages?: ProductPage[],
  smartCarts?: SmartCart[],
  checkoutProfiles?: CheckoutProfile[],
): TestVariant {
  // Check if this is an extension test
  const extensionWidgets =
    abTestType === AbTestTypeCategory.EXTENSIONS
      ? (variant.abTestWidgets?.filter((w: AbTestWidget) => w.type === AbTestWidgetType.EXTENSION) ?? [])
      : [];
  const extensionGroupWidgets =
    abTestType === AbTestTypeCategory.EXTENSIONS_GROUPS
      ? (variant.abTestWidgets?.filter((w: AbTestWidget) => w.type === AbTestWidgetType.EXTENSION_GROUPS) ?? [])
      : [];
  const themeTestWidgets = getIsThemeTest(abTestType)
    ? (variant.abTestWidgets?.filter((w: AbTestWidget) => getIsThemeWidget(w.type)) ?? [])
    : [];
  const checkoutWidgets =
    variant.abTestWidgets?.filter((w: AbTestWidget) => w.type === AbTestWidgetType.CHECKOUT) ?? [];

  // Handle extension tests
  if (abTestType === AbTestTypeCategory.EXTENSIONS && extensionWidgets.length > 0) {
    const variantExtensionIds = extensionWidgets.map((w: AbTestWidget) => w.externalId);
    const isNoneExtension = variantExtensionIds.every((id) => id === null);
    const variantExtensions = variantExtensionIds
      .filter((id) => id !== null)
      .map((id) => extensions.find((e) => e.id === id));
    const variantExtensionNames = variantExtensions.map(
      (extension) => extension?.template?.internalName || 'Deleted extension',
    );

    return {
      variant: variant.name,
      description: describeExtensionVariantGroup(variant.name),
      checkoutGroupInfo: isNoneExtension ? 'No extension' : formatSelectedExtensionNames(variantExtensionNames),
      traffic: variant.percentageTraffic,
      convRate: metricsData?.convRate,
      arpc: metricsData?.arpc,
      asr: metricsData?.asr,
      aov: metricsData?.aov,
      checkouts: metricsData?.checkouts,
      isControl: variant.name === 'A',
      isWinning: !!variant.winnerAt,
    };
  }

  if (abTestType === AbTestTypeCategory.EXTENSIONS_GROUPS && extensionGroupWidgets.length > 0) {
    const currentExtensionGroupId = extensionGroupWidgets[0]?.externalId;
    const currentExtensionGroup = extensionGroups.find((g) => g.id === currentExtensionGroupId);
    const isNoneExtensionGroup = currentExtensionGroupId === null;

    return {
      variant: variant.name,
      description: isNoneExtensionGroup ? 'NONE' : currentExtensionGroup?.name || 'Deleted extension group',
      checkoutGroupInfo: isNoneExtensionGroup
        ? 'No extension group'
        : currentExtensionGroup?.description || 'Extension group',
      traffic: variant.percentageTraffic,
      convRate: metricsData?.convRate,
      arpc: metricsData?.arpc,
      asr: metricsData?.asr,
      aov: metricsData?.aov,
      checkouts: metricsData?.checkouts,
      isControl: variant.name === 'A',
      isWinning: !!variant.winnerAt,
    };
  }

  // Handle theme tests (cross-site, product page, smart cart)
  if (getIsThemeTest(abTestType) && themeTestWidgets.length > 0) {
    const externalId = themeTestWidgets[0]?.externalId;

    let description: string;
    let checkoutGroupInfo: string;

    if (abTestType === AbTestTypeCategory.CROSS_SITE) {
      const currentTheme = themes?.find((t) => Number(t.id) === Number(externalId));
      description = currentTheme?.name || 'Deleted theme';
      checkoutGroupInfo = currentTheme?.updatedAt
        ? `Last saved: ${moment(currentTheme.updatedAt).format('MMM D [at] h:mm a')}`
        : 'Theme';
    } else if (abTestType === AbTestTypeCategory.SMART_CART) {
      const currentSmartCart = smartCarts?.find((s) => Number(s.id) === Number(externalId));
      description = currentSmartCart?.name || 'Deleted Smart Cart';
      checkoutGroupInfo = currentSmartCart?.description || 'Smart Cart';
    } else {
      // PRODUCT_PAGE
      const currentProductPage = productPages?.find((p) => Number(p.id) === Number(externalId));
      const isNoProductPromise = externalId === null;
      description = isNoProductPromise ? 'No Product Promise' : currentProductPage?.name || 'Deleted product promise';
      checkoutGroupInfo = isNoProductPromise
        ? 'No product promise'
        : currentProductPage?.internalDescription || 'Product Promise';
    }

    return {
      variant: variant.name,
      description,
      checkoutGroupInfo,
      traffic: variant.percentageTraffic,
      users: metricsData?.users,
      statSig: metricsData?.statSig,
      cvr: metricsData?.cvr,
      arpu: metricsData?.arpu,
      aov: metricsData?.aov,
      isControl: variant.name === 'A',
      isWinning: !!variant.winnerAt,
    };
  }

  const checkoutProfileWidget = getIsCheckoutProfileTest(abTestType)
    ? variant.abTestWidgets?.find((w: AbTestWidget) => w.type === AbTestWidgetType.CHECKOUT_PROFILE)
    : undefined;
  if (checkoutProfileWidget) {
    const currentCheckoutProfile = checkoutProfiles?.find(
      (profile) => Number(profile.id) === Number(checkoutProfileWidget.externalId),
    );

    return {
      variant: variant.name,
      description: currentCheckoutProfile?.name || DELETED_CHECKOUT_PROFILE_NAME,
      checkoutGroupInfo: 'Checkout profile',
      traffic: variant.percentageTraffic,
      users: metricsData?.users,
      statSig: metricsData?.statSig,
      cvr: metricsData?.cvr,
      arpu: metricsData?.arpu,
      aov: metricsData?.aov,
      isControl: variant.name === 'A',
      isWinning: !!variant.winnerAt,
    };
  }

  // Handle checkout tests (existing logic)
  const currentCheckoutId = checkoutWidgets[0]?.externalId;
  const currentCheckout = checkouts.find((c) => c.id === currentCheckoutId);
  const checkoutIds = checkoutWidgets.map((w) => w.externalId);
  const checkoutsWidgets = checkouts.filter((c) => checkoutIds.includes(c.id));
  const isMultipleControlGroup =
    abTestMode === AbTestGroupMode.MULTIPLE_CONTROL_GROUPS && variant.name === 'A' && checkoutsWidgets?.length > 1;

  return {
    variant: variant.name,
    description: isMultipleControlGroup ? 'Control Group' : currentCheckout?.name || 'Deleted checkout',
    checkoutGroupInfo: isMultipleControlGroup
      ? 'Test checkout group'
      : currentCheckout?.description || 'Test checkout group',
    traffic: variant.percentageTraffic,
    // These would come from metrics data in real implementation
    convRate: metricsData?.convRate, // Default 50% as shown in Figma
    arpc: metricsData?.arpc, // Default from Figma
    asr: metricsData?.asr,
    aov: metricsData?.aov,
    checkouts: metricsData?.checkouts,
    isControl: variant.name === 'A', // Assuming 'A' is control
    isWinning: !!variant.winnerAt,
  };
}

export default function TestVariantsTable({
  variants,
  className = '',
  isDraftMode,
  isLoading,
  showSetWinnerButton = false,
  onSetWinner,
  abTestVariants = [],
  testType = AbTestTypeCategory.SHIPPING_RATES,
  abTestId,
}: TestVariantsTableProps) {
  // Determine columns based on test type
  const isThemeTest = getIsThemeTest(testType) || getIsCheckoutProfileTest(testType);
  const tableHeaders = isDraftMode ? ['Variant'] : isThemeTest ? CROSS_SITE_TABLE_HEADERS : TABLE_HEADERS;
  const tableColumns = isThemeTest ? CROSS_SITE_TABLE_COLUMNS : TABLE_COLUMNS;
  const hasWinner = abTestVariants.some((v) => v.winnerAt);

  // Create a lookup map for O(1) access instead of O(n) .find() in renderRow
  const variantIdMap = useMemo(() => {
    const map = new Map<string, number>();
    abTestVariants.forEach((variant) => {
      if (variant.id && variant.name) {
        map.set(variant.name, variant.id);
      }
    });
    return map;
  }, [abTestVariants]);

  const renderHeader = () => (
    <>
      {tableHeaders.map((header) => {
        const tooltipText = getTooltipText(header, isThemeTest);

        return (
          <div className="generic-metrics-table__head-cell" key={header}>
            {header}
            {tooltipText && (
              <Tooltip tooltipText={tooltipText}>
                <i className="icon icon-more_information" />
              </Tooltip>
            )}
          </div>
        );
      })}
      {showSetWinnerButton && <div className="generic-metrics-table__head-cell">Action</div>}
    </>
  );

  const renderRow = (variant: TestVariant) => {
    const variantId = variantIdMap.get(variant.variant);
    const canShowSetWinnerButton = variantId && onSetWinner;
    const previewString = getAbTestVariantPreviewString(testType, abTestId, variant.variant);

    return (
      <>
        <TableCell>
          <div className="test-variant-info">
            <span className="test-variant-info__letter">{variant.variant}</span>
            <div className="test-variant-info__content">
              <div className="test-variant-info__name-row">
                <span className="test-variant-info__name">{variant.description}</span>
                {previewString && <VariantPreviewCopyButton previewString={previewString} />}
              </div>
              <span className="test-variant-info__description">{variant.checkoutGroupInfo}</span>
            </div>
            {hasWinner && variant.isWinning && (
              <StatusText className="test-variant-info__winner-label" label={'Winner'} color={'hyperlight20'} />
            )}
          </div>
        </TableCell>
        {!isDraftMode && (
          <>
            {tableColumns.map(({ key, type }) => {
              const value = (formatters as any)[type](variant[key]);
              return (
                <TableCell
                  key={key}
                  value={value || '-'}
                  className={type === 'currency' ? formatters.getCurrencyColorClass(variant[key]) : ''}
                  isLoading={isLoading}
                />
              );
            })}
          </>
        )}
        {showSetWinnerButton && (
          <TableCell>
            {canShowSetWinnerButton ? (
              <Button
                onClick={() => onSetWinner(variantId!)}
                disabled={hasWinner}
                text={getWinnerButtonText(testType)}
                size={'medium'}
                variant={'tertiary'}
              />
            ) : (
              <span>-</span>
            )}
          </TableCell>
        )}
      </>
    );
  };

  return (
    <GenericMetricsTable
      title="Test variants"
      data={variants}
      className={`test-variants-table ${className}`}
      renderHeader={renderHeader}
      renderRow={renderRow}
      keyExtractor={(variant) => variant.variant}
      isLoading={isLoading}
    />
  );
}
